import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, setRole, hasSession } from '../utils/api';
import { useI18n } from '../i18n/I18nProvider';
import AuthIllustration from '../components/AuthIllustration';
import BrandMark from '../components/BrandMark';
import { showMigrationNotice } from '../utils/migrationNotice';

const LoginPage = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [migrationNotice, setMigrationNotice] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    if (hasSession()) navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMigrationNotice(false);
    setLoading(true);
    try {
      const res = await api.login(email, password);
      if (res.ok) {
        // The session itself arrives as an httpOnly cookie (PIN-320); only the
        // role is ours to remember, and it is not a secret.
        const data = await res.json();
        setRole(data.role);
        navigate('/dashboard', { replace: true });
      } else {
        // Always show a localized message — never the raw English backend
        // string. A failed login is, by definition, bad credentials (PIN-252).
        setError(t('auth.invalidCredentials'));
        setMigrationNotice(showMigrationNotice());
      }
    } catch {
      setError(t('auth.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="uh-login-container">
      <div className="uh-login-card">
        <div className="uh-login-illustration">
          <AuthIllustration />
        </div>
        <div className="uh-login-form">
          <div className="uh-login-header">
            <div className="uh-login-greeting">{t('auth.welcome')}</div>
            <div className="uh-login-subtitle"><BrandMark size={28} />{t('app.brand')}</div>
          </div>
          <div className="uh-login-form-container">
            <div className="uh-login-form-title">{t('auth.signIn')}</div>
            {error && <div className="uh-alert-error">{error}</div>}
            {migrationNotice && (
              <div className="uh-alert-info" role="note">
                {t('auth.migrationNotice')} <Link to="/register">{t('auth.migrationNoticeLink')}</Link>.{' '}
                {t('auth.migrationNoticeSorry')}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="uh-login-field">
                <label htmlFor="email" className="uh-login-label">{t('auth.email')}</label>
                <input id="email" type="email" className="uh-login-input" value={email}
                  onChange={e => setEmail(e.target.value)} required disabled={loading} />
              </div>
              <div className="uh-login-field">
                <label htmlFor="password" className="uh-login-label">{t('auth.password')}</label>
                <input id="password" type="password" className="uh-login-input" value={password}
                  onChange={e => setPassword(e.target.value)} required disabled={loading} />
              </div>
              <div className="uh-login-buttons">
                <button type="submit" className="uh-btn uh-btn-primary" disabled={loading}>
                  {loading ? t('auth.signingIn') : t('auth.signIn')}
                </button>
              </div>
            </form>
            <div className="uh-login-alt">
              {t('auth.noAccount')} <Link to="/register">{t('auth.register')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
